import { animate, style, transition, trigger } from '@angular/animations';
import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

import { AxpoDialogComponent } from '../../../../core/axpo-dialog/axpo-dialog.component';
import { AxpoSpinnerComponent } from '../../../../core/axpo-spinner/axpo-spinner.component';
import { AxpoTypographyComponent } from '../../../../core/axpo-typography/axpo-typography.component';
import { ClickOutsideDirective } from '../../../directives/click-outside.directive';
import { ILocationModel } from '../../../models/api_models';
import { FormDebounceService } from '../../../services/form-debounce.service';

export interface IAutocompleteEntry {
  id: number | string;
  title: string;
  subtitle: string | undefined | null;
  value: string | number | undefined;
  originObject: ILocationModel;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-location-autocomplete',
  templateUrl: './location-autocomplete.component.html',
  imports: [
    TranslocoPipe,
    ClickOutsideDirective,
    AxpoSpinnerComponent,
    AxpoTypographyComponent,
    AxpoDialogComponent,
    NgClass,
  ],
  animations: [
    trigger('transformOpacityScale', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(.95)' }),
        animate('100ms ease-out', style({ opacity: 1, transform: 'scale(1)' })),
      ]),
      transition(':leave', [
        style({ opacity: 1, transform: 'scale(1)' }),
        animate('75ms ease-in', style({ opacity: 0, transform: 'scale(.95)' })),
      ]),
    ]),
  ],
})
export class LocationAutocompleteComponent {
  autocompleteTextbox = viewChild<ElementRef>('autocompleteTextbox');
  translocoService = inject(TranslocoService);
  private debounceService = inject(FormDebounceService);
  entries = input.required<any[]>();
  selectedEntries = model.required<any[]>();
  isLoading = input.required<boolean>();
  placeholder = input<string | undefined>();
  debounceTime = input<number>(500);
  entryAdded = output<any>();
  entryRemoved = output<any>();
  searchTextChanged = output<string>();
  debounceValue = output<string | undefined>();
  locationSearchText = signal<string | undefined>('');
  isMenuOpen = signal<boolean>(false);
  entriesToBeAdded = signal<any[]>([]);
  entriesToBeRemoved = signal<any[]>([]);
  parentFormId = input<string | undefined>(undefined);
  required = input<boolean>(false);
  disabled = input<boolean>(false);
  showRequiredError = signal<boolean>(false);
  isLocations = input<boolean>(true);

  hideSuggestions = computed(() => {
    if (this.isLoading()) {
      return false;
    } else {
      return (
        this.locationSearchText()?.length === 0 ||
        this.entries().length === 0 ||
        this.locationSearchText() === undefined ||
        !this.isMenuOpen()
      );
    }
  });

  removeEntry = (entry: any) => {
    if (this.required() && this.selectedEntries().length === 1 && this.isLocations()) {
      this.showRequiredError.set(true);
      return;
    }

    const copy = [...this.selectedEntries()];
    copy.splice(copy.indexOf(entry), 1);
    this.selectedEntries.set(copy);

    const toBeRemoved = [...this.entriesToBeRemoved()];
    toBeRemoved.push(entry);

    this.entryRemoved.emit(entry);
  };

  addEntry = (entry: any) => {
    const copy = [...this.selectedEntries()];
    copy.push(entry);
    this.selectedEntries.set(copy);

    const toBeAdded = [...this.entriesToBeAdded()];
    toBeAdded.push(entry);

    this.entryAdded.emit(entry);

    this.locationSearchText.set('');
    this.isMenuOpen.set(false);
    const textbox = this.autocompleteTextbox()?.nativeElement;
    if (textbox) {
      textbox.value = '';
    }
  };

  focus = () => {
    this.autocompleteTextbox()?.nativeElement.focus();
    this.isMenuOpen.set(true);
  };

  keyup = (): void => {
    this.locationSearchText.set(this.autocompleteTextbox()?.nativeElement.value);
  };

  clickOutside = (): void => {
    this.isMenuOpen.set(false);
  };

  afterLocationError(_$event: any) {
    this.showRequiredError.set(false);
  }

  _debounce = effect(onCleanup => {
    const value = this.locationSearchText();
    untracked(() => {
      const parentFormId = this.parentFormId();
      if (parentFormId) {
        this.debounceService.setActive(parentFormId);
      }

      const timeout = setTimeout(() => {
        this.debounceValue.emit(value);
        if (parentFormId) {
          this.debounceService.setInactive(parentFormId);
        }
      }, 500);
      onCleanup(() => clearTimeout(timeout));
    });
  });
}
